/**
 * scheduleSync — debounced post-commit push trigger.
 *
 * After a local billing/inventory commit, call `scheduleSyncPush()` to flush the
 * change to the server soon (which in turn wakes peer devices). Debounced so a
 * burst of sales coalesces into a single sync, respecting the server throttle.
 *
 * Fully decoupled from the data path: the local WatermelonDB write is already
 * the source of truth. If this push is skipped, delayed, or fails, the change
 * still ships on the next focus/network/peer-wake sync — no data is lost
 * (realtime-sync-zero-data-loss). NEVER call this inside a database.write().
 */
import { syncDatabase } from './index';

const DEBOUNCE_MS = 2000;
let timer: ReturnType<typeof setTimeout> | null = null;

export function scheduleSyncPush(debounceMs = DEBOUNCE_MS): void {
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => {
        timer = null;
        // syncDatabase is guarded against concurrent runs, so this coalesces with
        // any in-flight sync (focus/network/peer-wake). Errors are non-fatal.
        void syncDatabase().catch((e) =>
            console.warn('[scheduleSync] post-commit push failed (non-fatal):', (e as Error).message),
        );
    }, debounceMs);
}

/** Cancel a pending push (e.g. on logout). */
export function cancelScheduledSyncPush(): void {
    if (timer) {
        clearTimeout(timer);
        timer = null;
    }
}
